import React, { Component } from 'react';
import { isEqual } from 'lodash';
import DateRangeLink from 'containers/buttons/DateRangeLink';
import PropTypes from 'prop-types';
import ExportableTable from 'containers/tables/ExportableTable';
import helpers from 'helpers';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { insertBeforeItem } from 'helpers/utilities';
import { generateFooterData, generateHeaders } from 'helpers/tables';

// Constants
import { MAGIC_TOP_LEVEL_GEO } from 'constants/labs';

const DEFAULT_ACCESSORS = ['reach', 'ecpa_activities', 'ecpa'];

class LabsCountrybyProductPivotTable extends Component {
    constructor(props) {
        super(props);

        this.entityDashboardLink = this.entityDashboardLink.bind(this);

        const { data, columns } = LabsCountrybyProductPivotTable.createTableData(props.data, props);

        this.state = {
            data,
            columns,
            accessors: props.accessors || DEFAULT_ACCESSORS,
            showTotalActivities: props.showTotalActivities,
            title: props.title,
            entityId: props.entityId
        };
    }

    static createTableData(data, props) {
        const headers = generateHeaders({
            keys: [
                'campaign_activities',
                'clicks',
                'completion_rate',
                'cpc',
                'cpcv',
                'ctr',
                'ecpa',
                'ecpa_activities',
                'impressions',
                'spend',
                'ecpa_spend',
                'total_activities',
                'reach'
            ],
            colOptions: {
                labelCode: props.entityObjectiveLabelCode
            },
            footerData: generateFooterData({ data, filteredKeys: ['entity_name', 'entity_id'] })
        });

        let { accessors = DEFAULT_ACCESSORS } = props;
        if (props.showTotalActivities && !accessors.includes('total_activities')) {
            if (accessors.includes('campaign_activities')) {
                accessors = insertBeforeItem(accessors, 'campaign_activities', 'total_activities');
            } else {
                accessors = insertBeforeItem(accessors, 'ecpa_activities', 'total_activities');
            }
        }

        const columns = accessors.map(accessor => headers[accessor]);

        return { data, columns };
    }

    entityDashboardLink(row) {
        return (
            <DateRangeLink
                to={`/dashboard/${row.original.entity_id}/summary`}
                target='_blank'
                title='Go to dashboard'>
                {row.original.entity_name}
            </DateRangeLink>
        );
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps.data, this.props.data) || !isEqual(nextProps.country, this.props.country);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextData = nextProps.data;
        const nextEntityId = nextProps.entityId;
        const nextTitle = nextProps.title;
        const nextAccessors = nextProps.accessors;
        const nextShowTotalActivities = nextProps.showTotalActivities;

        if (nextData !== prevState.data
            || nextEntityId !== prevState.entityId
            || nextAccessors !== prevState.accessors
            || nextShowTotalActivities !== prevState.showTotalActivities
        ) {
            return {
                ...prevState,
                data: nextData,
                accessors: nextAccessors,
                showTotalActivities: nextShowTotalActivities,
                entityId: nextEntityId,
                title: nextTitle
            };
        }
        return null;
    }

    render() {
        const { data, columns, title } = this.state;
        const { aliasDescriptor, exportName, country } = this.props;

        const expanded = country === MAGIC_TOP_LEVEL_GEO ? {} : { 0: true }; // WARNING: HARD CODED

        return (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader hideCSVExport={true} type={'table'}>{title}</ContentHeader>
                <ExportableTable
                    key={1}
                    title={title}
                    exportName={exportName}
                    data={data.filter((e) => {
                        if (country === MAGIC_TOP_LEVEL_GEO) { // WARNING: HARD CODED
                            return true;
                        }
                        return e.entity_name === country;
                    })}
                    expanded={country === MAGIC_TOP_LEVEL_GEO ? undefined : expanded} // WARNING: HARD CODED
                    columns={[
                        {
                            Header: `${aliasDescriptor}`,
                            accessor: 'entity_name',
                            Cell: ({ value }) => <CellDiv value={value} />,
                            PivotValue: ({ value }) => <span>{value}</span>,
                            Footer: (
                                <CellDiv value='Total' strong />
                            )
                        },
                        {
                            Header: 'PRODUCT',
                            accessor: 'product',
                            Cell: ({ value }) => <CellDiv value={value} style={{ textAlign: 'right' }} />,
                            Aggregated: (row) => {
                                const count = row.subRows.length;
                                let value = `${count} Products`;
                                if (count === 1) {
                                    value = row.subRows[0].product;
                                } else if (row.isExpanded) {
                                    value = '';
                                }
                                return (
                                    <CellDiv
                                        strong={count !== 1}
                                        value={value}
                                        style={{ textAlign: 'right' }}
                                    />
                                );
                            },
                            Footer: (
                                <CellDiv value='' strong />
                            )
                        },
                        ...columns
                    ]}
                    getTfootProps= {() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    pivotBy={['entity_name']}
                    defaultSorted={[
                        {
                            id: 'entity_name',
                            desc: false
                        }
                    ]}
                    showPagination={false}
                    PaginationComponent={Pagination}
                    defaultPageSize={RowsPerPageDefault}
                    minRows={country === MAGIC_TOP_LEVEL_GEO ? 6 : 1} // WARNING: HARD CODED
                    className="-highlight"
                />
            </ContentTile>
        );
    }
}

LabsCountrybyProductPivotTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        entity_name: PropTypes.string,
        impressions: PropTypes.number,
        total_activities: PropTypes.number,
        ecpa_activities: PropTypes.number,
        ecpa: PropTypes.number,
        spend: PropTypes.number
    })),
    title: PropTypes.string.isRequired,
    entityObjectiveLabelCode: PropTypes.string
};

export default LabsCountrybyProductPivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/labs/LabsCountrybyProductPivotTable.js